import type { QueryClient } from '@tanstack/react-query';
import { lazyMapFor } from './lazyMapFor';

export type ExpiringIdRegistry = {
  /** Marks each id, refreshing its expiry even if already present. */
  record: (queryClient: QueryClient, ids: readonly number[]) => void;
  /** Reverses a `record` immediately, before its TTL — e.g. a mutation that never actually happened. */
  forget: (queryClient: QueryClient, ids: readonly number[]) => void;
  /** Sweeps every expired entry for this client as a side effect, not just the id being checked. */
  has: (queryClient: QueryClient, id: number) => boolean;
  /** Sweeps every expired entry for this client as a side effect, so the map never grows unbounded. */
  ids: (queryClient: QueryClient) => ReadonlySet<number>;
};

/**
 * A per-`QueryClient` `Map<id, expiresAt>`, evicted lazily on read rather than by timer. The
 * `WeakMap` keying means nothing needs to be torn down when a client is discarded. For "this id
 * was touched recently, by this client" needing a bound shorter than the client's own lifetime —
 * self-edit attribution, an explicit-delete bypass.
 */
export const createExpiringIdRegistry = (ttlMs: number): ExpiringIdRegistry => {
  const expiryByIdByClient = new WeakMap<QueryClient, Map<number, number>>();

  /** Removes every expired entry in place — shared so a point check bounds the map as well as a bulk read. */
  const sweepExpired = (expiryById: Map<number, number>): void => {
    const now = Date.now();
    for (const [id, expiresAt] of expiryById) {
      if (expiresAt <= now) expiryById.delete(id);
    }
  };

  const record: ExpiringIdRegistry['record'] = (queryClient, ids) => {
    if (ids.length === 0) return;
    const expiryById = lazyMapFor(expiryByIdByClient, queryClient);
    // A client that never calls has()/ids() would otherwise let expired ids pile up forever.
    sweepExpired(expiryById);
    const expiresAt = Date.now() + ttlMs;
    for (const id of ids) expiryById.set(id, expiresAt);
  };

  const forget: ExpiringIdRegistry['forget'] = (queryClient, ids) => {
    const expiryById = expiryByIdByClient.get(queryClient);
    if (!expiryById) return;
    for (const id of ids) expiryById.delete(id);
  };

  const has: ExpiringIdRegistry['has'] = (queryClient, id) => {
    const expiryById = expiryByIdByClient.get(queryClient);
    if (!expiryById) return false;
    sweepExpired(expiryById);
    return expiryById.has(id);
  };

  const ids: ExpiringIdRegistry['ids'] = (queryClient) => {
    const expiryById = expiryByIdByClient.get(queryClient);
    if (!expiryById) return new Set();
    sweepExpired(expiryById);
    return new Set(expiryById.keys());
  };

  return { record, forget, has, ids };
};
